import { type FormatOptionLabelMeta } from 'react-select';
import { Trans, useLingui } from '@lingui/react/macro';

import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import InputText from '@/design_system/InputText';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import { type Brand } from '@/models/brand';

import { PleaseSelectAnOptionError } from './PleaseSelectAnOptionError';

export const BrandInputs = ({
  brandOptions,
  selectedBrand,
  otherBrandName,
  onBrandSelect,
  onOtherBrandChange,
  showOtherBrandOption = true,
  showLabel = true,
  showErrors = false,
}: {
  brandOptions: Brand[];
  selectedBrand?: Brand;
  otherBrandName?: string;
  onBrandSelect: (brand: { id: string } | null) => void;
  onOtherBrandChange?: (otherBrandName?: string) => void;
  showOtherBrandOption?: boolean;
  showLabel?: boolean;
  showErrors?: boolean;
}) => {
  const { t } = useLingui();

  const regularBrands = brandOptions.filter((brand) => !brand.isOtherBrand);
  const otherBrand = brandOptions.find((brand) => !!brand.isOtherBrand);

  const options =
    otherBrand && showOtherBrandOption
      ? [...regularBrands, { ...otherBrand, isStickyOption: true }]
      : regularBrands;

  return (
    <Stack gap="0.25rem">
      <Stack row mobileColumn gap="1rem">
        <InputSearchSelect
          style={{ flex: 1 }}
          label={
            showLabel
              ? t({
                  id: 'brand-inputs.brand.select-brand.label',
                  message: 'Select the brand of the item',
                })
              : undefined
          }
          placeholder={t({
            id: 'brand-inputs.brand.select-brand.placeholder',
            message: 'Search brand by name...',
          })}
          value={selectedBrand}
          options={options}
          onChange={onBrandSelect}
          getOptionLabel={(brand) => brand.name}
          getOptionValue={(brand) => brand.id}
          formatOptionLabel={(brand, context) => (
            <FormattedBrandOptionLabel brand={brand} context={context} />
          )}
          isInvalid={showErrors && !selectedBrand}
        />

        {selectedBrand?.isOtherBrand && (
          <InputText
            style={{ flex: 1 }}
            label={t({
              id: 'brand-inputs.other.specify-name.label',
              message: 'Brand name',
            })}
            placeholder={t({
              id: 'brand-inputs.other.specify-name.placeholder',
              message: 'Specify the brand name',
            })}
            value={otherBrandName}
            onChange={onOtherBrandChange}
            isInvalid={showErrors && !!selectedBrand && !otherBrandName}
            onMount={(ref) => {
              // If the user just selected the "Other" brand
              // then focus on the text input once it appears
              if (!!ref && 'current' in ref) {
                ref.current?.focus();
              }
            }}
          />
        )}
      </Stack>

      {showErrors && !selectedBrand && <PleaseSelectAnOptionError />}
      {showErrors && !!selectedBrand?.isOtherBrand && !otherBrandName && (
        <Message type="error" showMessageIcon>
          <Trans id="brand-inputs.other.specify-name.error">Please add a brand name</Trans>
        </Message>
      )}
    </Stack>
  );
};

const FormattedBrandOptionLabel = ({
  brand,
  context,
}: {
  brand: Brand;
  context: FormatOptionLabelMeta<Brand>;
}) => {
  if (brand.isOtherBrand) {
    if (context.context === 'menu') {
      return (
        <Stack row gap="0.5rem" alignItems="center" style={{ flex: 1 }}>
          <IconAdd style={{ fontSize: '1.25rem' }} />
          <Paragraph>
            <Trans id="article.form.brand.other">Other (specify)</Trans>
          </Paragraph>
        </Stack>
      );
    } else {
      return <Trans id="article.form.brand.other.label">Other</Trans>;
    }
  } else {
    return brand.name;
  }
};
